// src/App.js
//
// WHAT THIS FILE IS:
// This is the ROOT component - the very top of the whole app. It:
//  1. Keeps track of whether the user is logged in (the auth token)
//  2. Decides whether to show the Login page or the real dashboard
//  3. Sets up all the page routes (which URL shows which page)

import { useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
// BrowserRouter = wraps the whole app to enable URL-based navigation
// Routes = a container that holds all possible Route definitions
// Route = maps ONE url path to ONE component to render there

import Login from './Login';
import Sidebar from './Sidebar';
import ProjectsPage from './ProjectsPage';
import ActivitiesPage from './ActivitiesPage';
import StaffPage from './StaffPage';
import BeneficiariesPage from './BeneficiariesPage';
import TaskPage from './TaskPage';
import BudgetPage from './BudgetPage';
import ImpactRecordPage from './ImpactRecordPage';
import FeedbackPage from './FeedbackPage';
import AttachmentPage from './AttachmentPage';

function App() {
  // token starts as whatever is already saved in the browser's
  // localStorage (so refreshing the page doesn't log you out) - or null
  // if nothing is saved there yet, meaning "not logged in".
  const [token, setToken] = useState(localStorage.getItem('token'));

  // handleLogin runs when Login.js successfully gets a token back from
  // Django. We save it in TWO places:
  //  - localStorage: survives page refreshes/closing the browser
  //  - React state (setToken): triggers React to re-render immediately
  const handleLogin = (newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
  };

  // If there's no token, don't even set up routing - just show the
  // Login page and stop here. This is the simplest possible way to
  // "protect" the whole app behind a login screen.
  if (!token) {
    return <Login onLogin={handleLogin} />;
  }

  // Once logged in, render the real app: sidebar on the left, routed
  // page content on the right, wrapped in BrowserRouter so Sidebar's
  // <Link> components and useLocation() work correctly.
  return (
    <BrowserRouter>
      <div className="flex">
        <Sidebar />

        <div className="flex-1 p-6">
          {/* A visible logout button, always present above the page content */}
          <div className="flex justify-end mb-4">
            <button
              onClick={handleLogout}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
            >
              Log Out
            </button>
          </div>

          <Routes>
            {/* Each page component gets the token passed down as a prop,
                since each one needs it to attach to the Authorization
                header on its own fetch() calls. */}
            <Route path="/" element={<ProjectsPage token={token} />} />
            <Route path="/activities" element={<ActivitiesPage token={token} />} />
            <Route path="/staff" element={<StaffPage token={token} />} />
            <Route path="/beneficiaries" element={<BeneficiariesPage token={token} />} />
            <Route path="/tasks" element={<TaskPage token={token} />} />
            <Route path="/budgets" element={<BudgetPage token={token} />} />
            <Route path="/impact-records" element={<ImpactRecordPage token={token} />} />
            <Route path="/feedback" element={<FeedbackPage token={token} />} />
            <Route path="/attachments" element={<AttachmentPage token={token} />} />
          </Routes>
        </div>
      </div>
    </BrowserRouter>
  );
}

export default App;
